import { useCallback, useState } from 'react';

const CROSSFADE_MS = 300;

// Inject fade-in keyframe once at module load
(() => {
    if (typeof document === 'undefined') return;
    const style = document.createElement('style');
    style.textContent = '@keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }';
    document.head.appendChild(style);
})();

type FadeState = 'idle' | 'hidden' | 'animating';

/**
 * Hook for smooth crossfade transitions between list and detail views.
 * Returns a `fadeStyle` to apply to the transitioning containers and a
 * `startCrossfade` function that hides content, swaps it, waits for
 * the browser to paint, then fades in.
 *
 * Usage:
 *   const { fadeStyle, startCrossfade } = useCrossfade();
 *   // on select: startCrossfade(() => { setSelected(item); scrollToTop(); });
 *   // on JSX:    <div style={fadeStyle}>...content...</div>
 */
export function useCrossfade() {
    const [fadeState, setFadeState] = useState<FadeState>('idle');

    let fadeStyle: React.CSSProperties | undefined;
    if (fadeState === 'hidden') fadeStyle = { opacity: 0 };
    else if (fadeState === 'animating') fadeStyle = { animation: `fade-in ${CROSSFADE_MS}ms ease-in-out` };

    const startCrossfade = useCallback((applyChange: () => void) => {
        // Phase 1: render new content invisible
        setFadeState('hidden');
        applyChange();

        // Phase 2: after the browser paints, start the fade-in animation
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setFadeState('animating');
                setTimeout(() => setFadeState('idle'), CROSSFADE_MS);
            });
        });
    }, []);

    return { fadeStyle, startCrossfade };
}
